import { HTMLAttributes } from 'react';
import { useCustomNavigate } from '@/website/hooks/useCustomNavigate';
import { Routes } from '@/website/util/routes';
import { JoveButton } from '@jove-frontend/mui';
import { useJoveBreakpoint } from '@jove-frontend/uikit';
import { ArrowCircleRight } from '@mui/icons-material';
import { Box, Container, Typography } from '@mui/material';
import clsx from 'clsx';

const STEP_IMAGES = [
  'https://storage.googleapis.com/jove-public-images/website/landing-pages/universal/switch-to-jove-join-jove-1.png',
  'https://storage.googleapis.com/jove-public-images/website/landing-pages/universal/switch-to-jove-join-jove-2.png',
  'https://storage.googleapis.com/jove-public-images/website/landing-pages/universal/switch-to-jove-join-jove-3.png',
];

type HowToSwitchProps = HTMLAttributes<HTMLDivElement> & { post?: any };

const HowToSwitch = (props: HowToSwitchProps) => {
  const { className, post } = props;
  const { push } = useCustomNavigate();
  const { isMobileLayout } = useJoveBreakpoint();

  const handleSwitchToJove = () => {
    push({ path: Routes.switchToJoveUploadPolicyDocuments() });
  };

  const steps = [
    {
      step: 1,
      title: post?.howToSwitchStep1Title,
      description: post?.howToSwitchStep1Description,
      image: STEP_IMAGES[0],
    },
    {
      step: 2,
      title: post?.howToSwitchStep2Title,
      description: post?.howToSwitchStep2Description,
      image: STEP_IMAGES[1],
    },
    {
      step: 3,
      title: post?.howToSwitchStep3Title,
      description: post?.howToSwitchStep3Description,
      image: STEP_IMAGES[2],
    },
  ];

  return (
    <Box component={Container} className={clsx('flex flex-col items-center space-y-12 py-16', className)}>
      {/* Header Section */}
      <Box className="flex flex-col items-center space-y-3">
        <Typography className={clsx('text-primary-black font-serif font-black leading-tight', isMobileLayout ? 'text-3xl' : 'text-[52px]')}>
          {post?.howToSwitchTitle}
        </Typography>
        <Typography variant="h6" className="text-primary-black font-medium">
          {post?.howToSwitchSubtitle}
        </Typography>
      </Box>

      {/* Steps Section */}
      <Box className={clsx('flex w-full justify-center', isMobileLayout ? 'flex-col gap-6 px-4' : 'flex-row gap-[60px]')}>
        {steps.map((item) => (
          <Box key={item.step} className={clsx('flex flex-col items-center space-y-4', isMobileLayout ? 'w-full' : 'flex-1')}>
            {/* Image */}
            <Box
              component="img"
              src={item.image}
              alt={`Step ${item.step}`}
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: '12px',
              }}
            />

            {/* Step Number */}
            <Typography variant="subtitle1" className="text-primary-black font-bold">
              Step {item.step}
            </Typography>

            {/* Title */}
            <Typography variant="h5" className="text-primary-black text-center font-black">
              {item.title}
            </Typography>

            {/* Description */}
            <Typography variant="subtitle1" className="text-primary-black text-center font-medium leading-tight">
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Button */}
      <JoveButton size="large" variant="contained" endIcon={<ArrowCircleRight />} onClick={handleSwitchToJove}>
        {post?.howToSwitchButton}
      </JoveButton>
    </Box>
  );
};

export default HowToSwitch;
